import React, { createContext, useContext } from 'react';

import type {
	InvitedAppShortcutAllowedTargetAppKey,
	InvitedAppShortcutCollaborator,
} from '../../../common/types';

/** Per-app slice of collaborators returned by the shared lazy fetch. */
export type CollaboratorsByApp = Partial<
	Record<InvitedAppShortcutAllowedTargetAppKey, InvitedAppShortcutCollaborator[]>
>;

/**
 * Public state machine surfaced via `useInvitedAppCollaboratorsLazy()`.
 */
export type InvitedAppCollaboratorsLazyState =
	| { status: 'idle' }
	| { status: 'loading' }
	| { error: unknown; status: 'error' }
	| { collaboratorsByApp: CollaboratorsByApp; status: 'ready' };

export type PrefetchAll = () => void;

export const StateContext: React.Context<InvitedAppCollaboratorsLazyState> =
	createContext<InvitedAppCollaboratorsLazyState>({ status: 'idle' });

export const PrefetchContext: React.Context<PrefetchAll> = createContext<PrefetchAll>(() => {});

/**
 * Reads the shared collaborator fetch state. Always call unconditionally
 * (do not gate on collaborator source kind) so hook order stays stable.
 * In experiment mode the provider is not mounted, so this reads the
 * default `{ status: 'idle' }` — no re-renders since its identity is
 * fixed at module load.
 */
export const useInvitedAppCollaboratorsLazy = (): InvitedAppCollaboratorsLazyState =>
	useContext(StateContext);

/**
 * Returns the stable `prefetchAll` trigger from the lazy provider.
 * Calling it fires a singleton `getCollaboratorsInProducts` request for
 * all eligible apps (idempotent, retries on error).
 */
export const useInvitedAppCollaboratorsLazyPrefetch = (): PrefetchAll =>
	useContext(PrefetchContext);
